import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Pagination,
    Modal,
    IconButton,
    TextField,
    Button,
    Grid,
    InputAdornment
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import koLocale from 'date-fns/locale/ko';
import axios from 'axios';
import * as common from '../../../../commons/common';

const GuideCheckHistoryModal = ({ open, onClose, item }) => {
    const [confirmUserList, setConfirmUserList] = useState([]);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [isExporting, setIsExporting] = useState(false);

    // 검색 관련 상태
    const [searchName, setSearchName] = useState('');
    const [searchOrganization, setSearchOrganization] = useState('');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [searchParams, setSearchParams] = useState({
        noticeConfirmNm: '',
        organizationNm: '',
        startConfirmTime: null,
        endConfirmTime: null
    });

    useEffect(() => {
        if (open && item) {
            // 모달이 열릴 때 시작 날짜와 종료 날짜 모두 오늘 날짜로 설정
            const today = new Date();

            setStartDate(today);
            setEndDate(today);

            setPage(1);
            resetSearchFields(today, today);
            fetchData(today, today);
        }
    }, [open, item]);

    useEffect(() => {
        if (open && item) {
            fetchData();
        }
    }, [page, searchParams]);

    const resetSearchFields = (startDate = null, endDate = null) => {
        setSearchName('');
        setSearchOrganization('');
        setSearchParams({
            noticeConfirmNm: '',
            organizationNm: '',
            startConfirmTime: startDate ? new Date(startDate.setHours(0, 0, 0, 0)).toISOString() : null,
            endConfirmTime: endDate ? new Date(endDate.setHours(23, 59, 59, 999)).toISOString() : null
        });
    };

    const handleCloseAndReset = () => {
        setPage(1);
        resetSearchFields();
        onClose();
    };

    const handleSearch = () => {
        setPage(1);
        setSearchParams({
            noticeConfirmNm: searchName.trim(),
            // 시작 날짜의 경우 해당 날짜의 00:00:00으로 설정
            organizationNm: searchOrganization.trim(),
            startConfirmTime: startDate ? new Date(new Date(startDate).setHours(0, 0, 0, 0)).toISOString() : null,
            // 종료 날짜의 경우 해당 날짜의 23:59:59로 설정하여 하루 전체를 포함
            endConfirmTime: endDate ? new Date(new Date(endDate).setHours(23, 59, 59, 999)).toISOString() : null
        });
    };

    // 엑셀 다운로드 핸들러
    const handleExcelDownload = async () => {
        if (!item || !item.noticeId) {
            return;
        }

        setIsExporting(true);

        try {
            const token = localStorage.getItem('access_token');
            const url = `${common.getApiUrl()}/excel/notice/notice-confirm/${item.noticeId}`;

            const requestData = {
                noticeConfirmNm: searchParams.noticeConfirmNm,
                organizationNm: searchParams.organizationNm,
                startConfirmTime: searchParams.startConfirmTime,
                endConfirmTime: searchParams.endConfirmTime
            };

            const response = await axios({
                url,
                method: 'POST',
                responseType: 'blob',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                data: requestData
            });

            // 파일 다운로드 처리
            const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = downloadUrl;

            // 파일명 설정 (현재 날짜 포함)
            const date = new Date();
            const formattedDate = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`;
            link.setAttribute('download', `안전수칙이수현황_${item.noticeNm || '기본'}_${formattedDate}.xlsx`);

            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error('엑셀 다운로드 중 오류가 발생했습니다:', error);
            alert('엑셀 다운로드 중 오류가 발생했습니다.');
        } finally {
            setIsExporting(false);
        }
    };

    const fetchData = async (initialStartDate = null, initialEndDate = null) => {
        if (!item || !item.noticeId) {
            return;
        }

        try {
            const useInitialDates = initialStartDate && initialEndDate;

            const params = {
                page: page,
                pageSize: rowsPerPage,
                noticeConfirmNm: searchParams.noticeConfirmNm,
                organizationNm: searchParams.organizationNm,
                startConfirmTime: useInitialDates
                    ? new Date(initialStartDate.setHours(0, 0, 0, 0)).toISOString()
                    : searchParams.startConfirmTime,
                endConfirmTime: useInitialDates
                    ? new Date(initialEndDate.setHours(23, 59, 59, 999)).toISOString()
                    : searchParams.endConfirmTime
            };

            const response = await axios.get(`${common.getApiUrl()}/notice/notice-confirm/${item.noticeId}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('access_token')}`,
                },
                params
            });

            if (response.data && Array.isArray(response.data.content)) {
                let fetchedItems = response.data.content.map((item) => ({
                    userNm: item.noticeConfirmNm,
                    dateOfBirth: item.dateOfBirth,
                    organizationNm: item.organizationNm,
                    confirmDt: item.createdDt
                }));

                // 빈 행 추가 (페이지 크기를 일정하게 유지)
                while (fetchedItems.length < rowsPerPage) {
                    fetchedItems.push({
                        userNm: '',
                        dateOfBirth: '',
                        organizationNm: '',
                        confirmDt: ''
                    });
                }

                setConfirmUserList(fetchedItems);
                setTotalElements(response.data.totalElements);
            }

        } catch (error) {
            console.error('Error fetching confirmation details:', error);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    // 날짜 형식 변환 함수
    const formatDate = (date) => {
        if (!date) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '90%',
                    maxWidth: '1000px',
                    bgcolor: 'var(--main-white-color)',
                    boxShadow: 24,
                    p: 3,
                    borderRadius: 2,
                    outline: 'none',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
                        안전수칙 이수 기록
                    </Typography>
                    <IconButton size="small" onClick={handleCloseAndReset}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* 검색 영역 추가 */}
                <Paper sx={{ p: 2, mb: 3, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                    <Grid container spacing={2} alignItems="center">
                        {/* 시작 날짜  */}
                        <Grid item xs={12} md={2}>
                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={koLocale}>
                                <DatePicker
                                    value={startDate}
                                    onChange={(newValue) => setStartDate(newValue)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            size="small"
                                            fullWidth
                                            placeholder="시작 일자"
                                            label=""
                                            InputLabelProps={{ shrink: false }}
                                            sx={{
                                                '& .MuiInputBase-input': {
                                                    fontSize: '14px'
                                                }
                                            }}
                                        />
                                    )}
                                    inputFormat="yyyy-MM-dd"
                                />
                            </LocalizationProvider>
                        </Grid>

                        {/* 종료 날짜 */}
                        <Grid item xs={12} md={2}>
                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={koLocale}>
                                <DatePicker
                                    value={endDate}
                                    onChange={(newValue) => setEndDate(newValue)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            size="small"
                                            fullWidth
                                            placeholder="종료 일자"
                                            label=""
                                            InputLabelProps={{ shrink: false }}
                                            sx={{
                                                '& .MuiInputBase-input': {
                                                    fontSize: '14px'
                                                }
                                            }}
                                        />
                                    )}
                                    inputFormat="yyyy-MM-dd"
                                />
                            </LocalizationProvider>
                        </Grid>

                        {/* 이름 검색 필드 */}
                        <Grid item xs={12} md={2}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="이름 검색"
                                value={searchName}
                                onChange={(e) => setSearchName(e.target.value)}
                                variant="outlined"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                                label=""
                                InputLabelProps={{ shrink: false }}
                                sx={{
                                    '& .MuiInputBase-input': {
                                        fontSize: '14px'
                                    }
                                }}
                            />
                        </Grid>

                        {/* 소속 검색 필드 - 추가 */}
                        <Grid item xs={12} md={2}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="소속 검색"
                                value={searchOrganization}
                                onChange={(e) => setSearchOrganization(e.target.value)}
                                variant="outlined"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                                label=""
                                InputLabelProps={{ shrink: false }}
                                sx={{
                                    '& .MuiInputBase-input': {
                                        fontSize: '14px'
                                    }
                                }}
                            />
                        </Grid>

                        {/* 버튼 영역 */}
                        <Grid item container xs={12} md={3} spacing={1}>
                            <Grid item xs={6}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={handleSearch}
                                    sx={{
                                        height: '40px',
                                        backgroundColor: 'var(--main-blue-color)',
                                        '&:hover': { backgroundColor: '#1565C0' },
                                        boxShadow: 'none',
                                    }}
                                >
                                    검색
                                </Button>
                            </Grid>
                            <Grid item xs={6}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    startIcon={<FileDownloadIcon />}
                                    onClick={handleExcelDownload}
                                    disabled={isExporting}
                                    sx={{
                                        height: '40px',
                                        backgroundColor: 'var(--sub-excel-color)',
                                        '&:hover': { backgroundColor: '#388E3C' },
                                        boxShadow: 'none',
                                    }}
                                >
                                    엑셀
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </Paper>

                {/* 테이블 영역 */}
                <Paper sx={{ width: '100%', overflow: 'hidden', padding: '15px 10px 0 15px', boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                    <Box sx={{ bgcolor: '#fff', p: 3, borderRadius: 2, paddingY: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                            {item?.noticeNm || ''}
                        </Typography>
                        <TableContainer
                            sx={{
                                boxShadow: 'none',
                                border: 'none',
                                '& .MuiTable-root': {
                                    borderCollapse: 'separate',
                                    borderSpacing: 0,
                                    tableLayout: 'fixed',
                                    width: '100%'
                                }
                            }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        {['이름', '개인정보(인증)', '소속', '이수일시'].map((header, index) => (
                                            <TableCell
                                                key={index}
                                                sx={{
                                                    fontWeight: 'bold',
                                                    width: '25%',
                                                    backgroundColor: '#F2F2F2',
                                                    borderTop: '2px solid #909090',
                                                    borderBottom: '2px solid #909090',
                                                    borderLeft: 'none',
                                                    borderRight: 'none',
                                                    textAlign: 'center',
                                                    padding: '10px',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: ''
                                                }}
                                            >
                                                {header}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {confirmUserList && confirmUserList.map((user, index) => (
                                        <TableRow
                                            key={index}
                                            sx={{
                                                '&:last-child td': {
                                                    borderBottom: '2px solid #909090',
                                                },
                                                '& td': {
                                                    borderLeft: 'none',
                                                    borderRight: 'none',
                                                    textAlign: 'center',
                                                    padding: '10px',
                                                    height: '45px',
                                                    width: '25%',
                                                    whiteSpace: 'wrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }
                                            }}
                                        >
                                            <TableCell>{user.userNm}</TableCell>
                                            <TableCell>{user.dateOfBirth ? `${user.dateOfBirth}-*******` : ""}</TableCell>
                                            <TableCell>{user.organizationNm}</TableCell>
                                            <TableCell>{user.confirmDt}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 2, }}>
                            <Box>
                                <Pagination
                                    count={Math.ceil(totalElements / rowsPerPage)}
                                    page={page}
                                    onChange={(event, newPage) => handleChangePage(event, newPage)}
                                    showFirstButton
                                    showLastButton
                                />
                            </Box>
                        </Box>
                    </Box>
                </Paper>
            </Box>
        </Modal>
    );
};

export default GuideCheckHistoryModal;